import React, { useEffect, useState } from 'react';
import { portalApi } from '../../lib/parceiroApi';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Loader2, QrCode, CheckCircle2, FileDown } from 'lucide-react';
import { toast } from 'sonner';

const COR = {
  'Aberta': 'bg-blue-100 text-blue-700',
  'Fechada': 'bg-gray-100 text-gray-700',
  'Aguardando Pagamento': 'bg-amber-100 text-amber-800',
  'Paga': 'bg-green-100 text-green-700',
  'Vencida': 'bg-red-100 text-red-700',
  'Cancelada': 'bg-gray-100 text-gray-500',
};

export default function ParceiroFaturas() {
  const [faturas, setFaturas] = useState(null);
  const [filtro, setFiltro] = useState('todas');
  const [gerandoPix, setGerandoPix] = useState(null);
  const [confirmando, setConfirmando] = useState(null);

  const baixarPdf = async (f) => {
    try {
      const r = await portalApi.faturaPdf(f.id);
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${f.codigo}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao gerar PDF');
    }
  };

  const informarPagamento = async (f) => {
    setConfirmando(f.id);
    try {
      const r = await portalApi.confirmarPagamento(f.id);
      toast.success(r.data.message);
      const res = await portalApi.faturas();
      setFaturas(res.data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Não foi possível informar o pagamento');
    } finally {
      setConfirmando(null);
    }
  };

  const pagarPix = async (f) => {
    setGerandoPix(f.id);
    try {
      const r = await portalApi.pixFatura(f.id);
      window.open(`/pagar/${r.data.id}`, '_blank');
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Não foi possível gerar o PIX');
    } finally {
      setGerandoPix(null);
    }
  };

  useEffect(() => {
    portalApi.faturas().then((r) => setFaturas(r.data)).catch(() => setFaturas([]));
  }, []);

  if (!faturas) return <div className="flex justify-center py-20"><Loader2 className="animate-spin text-gray-400" size={28} /></div>;

  const emAberto = faturas.filter((f) => ['Aberta', 'Fechada', 'Aguardando Pagamento'].includes(f.status));
  const vencidas = faturas.filter((f) => f.status === 'Vencida');
  const pagas = faturas.filter((f) => f.status === 'Paga');
  const lista = filtro === 'abertas' ? emAberto : filtro === 'vencidas' ? vencidas : filtro === 'pagas' ? pagas : faturas;

  return (
    <div className="space-y-4" data-testid="parceiro-faturas">
      <h1 className="text-xl font-bold text-[#3D4F5F]">Faturas</h1>
      <div className="grid grid-cols-3 gap-2">
        <Resumo label="Em aberto" valor={emAberto.reduce((s, f) => s + f.total, 0)} cor="text-amber-600" onClick={() => setFiltro('abertas')} ativo={filtro === 'abertas'} />
        <Resumo label="Vencidas" valor={vencidas.reduce((s, f) => s + f.total, 0)} cor="text-red-600" onClick={() => setFiltro('vencidas')} ativo={filtro === 'vencidas'} />
        <Resumo label="Pagas" valor={pagas.reduce((s, f) => s + f.total, 0)} cor="text-green-600" onClick={() => setFiltro('pagas')} ativo={filtro === 'pagas'} />
      </div>
      {filtro !== 'todas' && <button className="text-xs text-[#2B7A3D] font-medium" onClick={() => setFiltro('todas')}>Ver todas</button>}

      {lista.length === 0 ? (
        <div className="bg-white rounded-xl border border-dashed border-gray-300 p-10 text-center text-sm text-gray-500">Nenhuma fatura.</div>
      ) : (
        <div className="space-y-2">
          {lista.map((f) => (
            <div key={f.id} className="bg-white rounded-xl border border-gray-200 p-3.5" data-testid={`fatura-${f.codigo}`}>
              <div className="flex items-center justify-between gap-2">
                <div>
                  <div className="text-sm font-bold text-[#3D4F5F]">{f.codigo}</div>
                  <div className="text-xs text-gray-500">{formatDate(f.periodo_inicio)} a {formatDate(f.periodo_fim)} · {f.ordens.length} OS</div>
                  <div className="text-xs text-gray-500">Vencimento: {formatDate(f.vencimento)}</div>
                </div>
                <div className="text-right">
                  <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${COR[f.status] || 'bg-gray-100 text-gray-600'}`}>{f.status}</span>
                  <div className="text-base font-bold text-[#3D4F5F] mt-1">{formatCurrency(f.total)}</div>
                  {f.desconto > 0 && <div className="text-[11px] text-green-600">desconto {formatCurrency(f.desconto)}</div>}
                </div>
              </div>
              <div className="flex items-center justify-between gap-2 mt-2 flex-wrap">
                <details>
                  <summary className="text-xs text-[#2B7A3D] font-medium cursor-pointer">Ver OS incluídas</summary>
                  <div className="mt-1 space-y-1">
                    {f.ordens.map((o) => (
                      <div key={o.ordem_id} className="flex justify-between text-xs text-gray-600 border-b border-gray-50 py-1 gap-4">
                        <span>#OS-{String(o.numero).padStart(4, '0')} · {o.cliente_nome}</span>
                        <span className="font-medium">{formatCurrency(o.valor)}</span>
                      </div>
                    ))}
                  </div>
                </details>
                {['Aberta', 'Fechada', 'Aguardando Pagamento', 'Vencida'].includes(f.status) && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <button onClick={() => pagarPix(f)} disabled={gerandoPix === f.id} data-testid={`pagar-pix-${f.codigo}`}
                      className="flex items-center gap-1.5 bg-[#2B7A3D] hover:bg-[#236632] text-white text-xs font-semibold px-3 py-2 rounded-lg">
                      {gerandoPix === f.id ? <Loader2 size={14} className="animate-spin" /> : <QrCode size={14} />} Pagar via PIX
                    </button>
                    {f.pagamento_informado ? (
                      <span className="flex items-center gap-1 text-[11px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1.5 rounded-lg" data-testid={`pagamento-informado-${f.codigo}`}>
                        <CheckCircle2 size={13} /> Pagamento informado — aguardando confirmação
                      </span>
                    ) : (
                      <button onClick={() => informarPagamento(f)} disabled={confirmando === f.id} data-testid={`ja-paguei-${f.codigo}`}
                        className="flex items-center gap-1.5 border border-[#2B7A3D] text-[#2B7A3D] hover:bg-emerald-50 text-xs font-semibold px-3 py-2 rounded-lg">
                        {confirmando === f.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />} Já paguei
                      </button>
                    )}
                  </div>
                )}

                <button onClick={() => baixarPdf(f)} data-testid={`pdf-fatura-${f.codigo}`}
                  className="flex items-center gap-1.5 text-xs font-semibold text-[#3D4F5F] border border-gray-200 hover:bg-gray-50 px-3 py-2 rounded-lg w-fit">
                  <FileDown size={14} /> Baixar PDF
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Resumo = ({ label, valor, cor, onClick, ativo }) => (
  <button onClick={onClick} className={`bg-white rounded-xl border p-3 text-center ${ativo ? 'border-[#2B7A3D]' : 'border-gray-200'}`}>
    <div className={`text-sm font-bold ${cor}`}>{formatCurrency(valor)}</div>
    <div className="text-[11px] text-gray-500 font-medium mt-0.5">{label}</div>
  </button>
);
